import { Link } from '@xing-com/crate-core-router';
import { useHost, useLoginAppUrl } from '@xing-com/crate-xinglet';
import { useRuntimeConfig } from '@xing-com/crate-xinglet/internal';
import type { LinkProps, ReactLinkHandler } from '@xing-com/hub';
import { ReactLinkContext } from '@xing-com/hub';

import { handleLinkWithDomain } from './handle-link-with-domain';
import { internalPaths } from './internal-paths';

type LinkTo = LinkProps['to'];

export function isInternal(url: string): boolean {
  return internalPaths.some((pattern) => pattern.test(url));
}

export function buildUrl(to: Exclude<LinkTo, undefined>): string {
  if (typeof to !== 'string') {
    const path = [to.pathname, to.search].filter(Boolean).join('?');

    to = [path, to.hash].filter(Boolean).join('#');
  }

  return to;
}

const handler: ReactLinkHandler = {
  Link({ to, href, children, ...props }) {
    const { getHostname } = useHost();
    const { entryPoint } = useRuntimeConfig();

    const loginAppUrl = useLoginAppUrl();
    const hostname = getHostname();

    const options = {
      entryPoint,
      loginAppUrl,
      hostname,
    };

    to = handleLinkWithDomain(to, options);
    href = handleLinkWithDomain(href, options);

    if (!to && !href) {
      return <a {...props}>{children}</a>;
    }

    const destination = to ?? href;
    if (!destination) {
      return <a {...props}>{children}</a>;
    }

    const url = buildUrl(destination);

    if (isInternal(url)) {
      return (
        <Link to={url} {...props}>
          {children}
        </Link>
      );
    } else {
      return (
        <a href={url} {...props}>
          {children}
        </a>
      );
    }
  },
};

export const ReachLinkModule: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  return (
    <ReactLinkContext.Provider value={handler}>
      {children}
    </ReactLinkContext.Provider>
  );
};
